/**
 * The in-meeting control bar's pill: a 44px-tall control that carries its own
 * label instead of wearing one underneath.
 *
 * Shared rather than written out four times because the row only reads as one
 * set while every pill in it is the same height and the same radius — and the
 * two files that draw them, `MeetingChat` and `CopilotFooterButton`, have
 * already drifted apart on exactly that once.
 *
 * Mic and pause are deliberately not pills. They are the transport controls,
 * they are understood from their icon alone, and spelling them out would push
 * the named actions off a narrow bar.
 *
 * `min-w-[2.25rem]` is what keeps a pill square once its label is hidden, so
 * the row below `lg` still lines up with the icon-only pair beside it.
 *
 * `px-3` and not `sm:px-4`: the compact padding keeps the remaining controls
 * inside the bar without taking a label or a control away.
 *
 * 36px tall rather than 44. Measured off the design: every control in it is
 * 34px, the mic and pause squares included, and 36 is the step the rest of the
 * product is drawn on — the Copilot question box among them. 44px was a touch
 * target sized for a label printed underneath; with the label inside, the pill
 * is already well past 44 in the direction a thumb has trouble with.
 */
export const MEETING_CONTROL_PILL =
  'flex h-9 min-w-[2.25rem] touch-manipulation items-center justify-center gap-2 rounded-xl px-3 text-xs font-semibold transition-colors'

/**
 * The label inside a pill. Hidden below `lg` rather than dropped: a label that
 * left the DOM would take the accessible name with it on any control without an
 * `aria-label` of its own.
 *
 * `lg` and not `sm`, which is where the labels under the buttons used to appear
 * — those cost no width and these do. Measured: the row needs ~759px with every
 * label showing, and the bar is only that wide from about 800px of viewport up.
 * At `sm` the labels appeared and flex squashed the mic from 44px to 29px to
 * make room. `lg` is also where this bar stops being the collapsible mobile
 * chrome and becomes the desktop one, so nothing is lost that was being shown.
 */
export const MEETING_CONTROL_PILL_LABEL = 'hidden whitespace-nowrap lg:block'

/**
 * The hairline between two groups of controls: the transport pair, the named
 * actions, and End Meeting on its own.
 *
 * 1px by 16px, measured off the design, which is deliberately shorter than the
 * 36px controls either side — a full-height rule would read as the edge of a
 * container rather than as a seam between two kinds of thing.
 *
 * Decorative, so it carries `aria-hidden`: the grouping is already in the
 * order and the labels, and a screen reader announcing two separators between
 * six buttons would be adding noise to a row it can already read.
 */
export const MEETING_CONTROL_DIVIDER =
  'h-4 w-px shrink-0 bg-slate-200 dark:bg-[var(--border-color)]'
